import { cn } from "../cn";

/**
 * Initials avatar.
 *
 * It owns **both** the initials and the colour, on purpose: those were derived
 * three different ways across screens — the sum of every character, the first
 * character alone, or a hard-coded class — so the same person changed colour
 * from one screen to the next. The palette exists precisely so they do not.
 */
export type AvatarSize = "sm" | "md" | "lg";
export type AvatarShape = "circle" | "rounded";

const SIZES: Record<AvatarSize, string> = {
  sm: "size-7 text-[10.5px]",
  md: "size-9 text-[12.5px]",
  lg: "size-[52px] text-[17px]",
};

/** Six palette entries, as tokens. `PALETTE.length` drives the cycle, so adding
    a seventh pair needs no arithmetic changed anywhere. */
const PALETTE = [1, 2, 3, 4, 5, 6] as const;

/** Stable across screens and sessions: every character counts, so two names
    sharing a first letter do not collide. */
function paletteIndex(seed: string): number {
  let sum = 0;
  for (const ch of seed) sum += ch.codePointAt(0) ?? 0;
  return PALETTE[sum % PALETTE.length] ?? 1;
}

export function initialsOf(name: string | null | undefined): string {
  if (!name?.trim()) return "··";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => [...p][0]?.toUpperCase() ?? "").join("") || "··";
}

export interface AvatarProps {
  /** Drives both the initials and the colour. */
  name: string | null | undefined;
  size?: AvatarSize;
  /** `rounded` for organizations, so a company never reads as a person. */
  shape?: AvatarShape;
  /** Override the initials when the label is not a person's name. */
  children?: string;
  className?: string;
  title?: string;
}

export function Avatar({
  name,
  size = "sm",
  shape = "circle",
  children,
  className,
  title,
}: AvatarProps) {
  const n = paletteIndex(name ?? "");
  return (
    <span
      title={title}
      style={{
        backgroundColor: `var(--color-avatar-${n})`,
        color: `var(--color-avatar-${n}-ink)`,
      }}
      className={cn(
        "grid flex-none place-items-center font-bold tracking-[0.02em]",
        shape === "circle" ? "rounded-full" : "rounded-lg",
        SIZES[size],
        className,
      )}
    >
      {children ?? initialsOf(name)}
    </span>
  );
}
